import React, { useEffect, useRef, useState } from "react";
import WebViewer, { WebViewerInstance, Core } from "@pdftron/webviewer";
import { ActionHandlerFunction } from "@preveil-api";
import { AppConfiguration, DriveErrorMessages, DriveItemType, DriveUIActionTypes, Message, MessageAnchors, MessageHandlerDisplayType, PDFTRON_CONFIG, PDFTRON_WEBVIEWER_OPTIONS, useAppDispatch } from "src/common";
import { uiActions } from "src/store";
/*
 * Documentation for adding disabling or enabling Features or ELements:
 * https://docs.apryse.com/web/guides/hiding-elements#finding-dataelement-attribute-values
 * https://github.com/ApryseSDK/webviewer-ui/blob/11.1/src/constants/dataElement.js
 * https://github.com/ApryseSDK/webviewer-ui/blob/11.1/src/constants/feature.js
 */
type AllProps = {
  file: File;
  type: string;
  is_view_only: boolean;
  handleAction: ActionHandlerFunction;
};

function FileViewerComponent(props: AllProps) {
  const { file, type, is_view_only, handleAction } = props;
  const dispatch = useAppDispatch();
  const disabled_elements = is_view_only ? PDFTRON_CONFIG.disabled_elements.concat(PDFTRON_CONFIG.view_only_elements) : PDFTRON_CONFIG.disabled_elements;
  const disabled_features = is_view_only ? PDFTRON_CONFIG.disabled_features.concat(PDFTRON_CONFIG.view_only_features) : PDFTRON_CONFIG.disabled_features;
  // NOTE: instance is only used in app build mode (non-iframe path)
  const [instance, setInstance] = useState<WebViewerInstance | undefined>(undefined);
  // NOTE: sw_ready tracks whether the PDF viewer Service Worker is active and controlling.
  //   Initialized to true in app mode (no SW needed) and false in web mode (must wait for SW).
  const [sw_ready, setSwReady] = useState<boolean>(!AppConfiguration.buildForWeb());
  const viewer = useRef<HTMLDivElement>(null);
  const iframe_ref = useRef<HTMLIFrameElement>(null);

  // NOTE: App build mode — initialize PDFTron directly in the parent document (no iframe, no SW).
  useEffect(() => {
    if (AppConfiguration.buildForWeb()) { return; }
    const options = { ...PDFTRON_WEBVIEWER_OPTIONS, disabledElements: disabled_elements };
    WebViewer(options, viewer.current as HTMLDivElement).then(instance => {
      const { UI } = instance;
      // NOTE: disable more if its view only
      UI.disableFeatures(disabled_features);

      // default to dark theme
      UI.setTheme("dark");
      // NOTE: Hide all modular headers for TIFF/TIF images before loading — no toolbar needed for image viewing.
      if (type === DriveItemType.Image) {
        UI.setModularHeaders([]);
      }
      setInstance(instance);

      // NOTE: Handle empty files (0 bytes) gracefully — skip PDFTron and show a warning modal.
      if (file.size === 0) {
        handleAction({ actionType: DriveUIActionTypes.SetLoading, params: false });
        UI.showWarningMessage({
          title: "Error Loading Document",
          message: `"${file.name}" is empty and cannot be previewed.`,
          confirmBtnText: "OK",
          onConfirm: () => { handleAction({ actionType: DriveUIActionTypes.ResetFileEntry }); },
          onCancel: () => { handleAction({ actionType: DriveUIActionTypes.ResetFileEntry }); },
        });
        return;
      }

      _loadDocument(instance, file);
    });
  }, []);

  // NOTE: Web build mode — wait for the PDF viewer SW to be active before rendering the iframe.
  //   The SW is scoped to "/" so it controls the current page. navigator.serviceWorker.ready
  //   resolves when the controlling SW is active — this is the simplest and most reliable check.
  useEffect(() => {
    if (!AppConfiguration.buildForWeb() || !("serviceWorker" in navigator)) { return; }

    // Description: Wait for the root-scoped PDF viewer SW to be active and controlling.
    navigator.serviceWorker.ready.then(() => setSwReady(true));
  }, []);

  // NOTE: Web build mode — set up the postMessage bridge with the /pdf-viewer/frame iframe.
  //   When the frame signals VIEWER_READY, transfer the file ArrayBuffer to it.
  //   When the frame signals DOCUMENT_LOADED, stop the loading spinner.
  useEffect(() => {
    if (!AppConfiguration.buildForWeb()) { return; }

    // Description: Handle messages received from the /pdf-viewer/frame iframe.
    async function handleFrameMessage(event: MessageEvent): Promise<void> {
      // NOTE: Validate origin before processing — defense-in-depth against spoofed postMessages.
      if (!event.data || !event.data.type) { return; }
      if (event.origin !== window.location.origin) {
        dispatch(uiActions.handleRequestErrors(
          new Message(DriveErrorMessages.error_invalid_pdf_viewer_origin.replace(MessageAnchors.origin, event.origin), MessageHandlerDisplayType.logger)
        ));
        return;
      }

      switch (event.data.type) {
        case "VIEWER_READY": {
          // NOTE: Transfer the file as an ArrayBuffer (zero-copy transferable).
          //   After transfer, the buffer is detached in the parent — this is intentional.
          const buffer = await file.arrayBuffer();
          // NOTE: Target origin "*" is intentional. The iframe is SW-served from the same origin,
          //   so no cross-origin secrets are exposed. Scoping to window.location.origin would be
          //   equivalent in production but would break local dev (port mismatch). File data is
          //   always sent to iframe_ref.current (the known iframe), not to the message sender.
          iframe_ref.current?.contentWindow?.postMessage(
            { type: "LOAD_FILE", data: buffer, file_name: file.name, file_type: type, webviewer_options: PDFTRON_WEBVIEWER_OPTIONS, pdftron_config: PDFTRON_CONFIG, disabled_elements, disabled_features },
            "*",
            [buffer]
          );
          break;
        }
        case "DOCUMENT_LOADED": {
          handleAction({ actionType: DriveUIActionTypes.SetLoading, params: false });
          break;
        }
        case "ERROR": {
          handleAction({ actionType: DriveUIActionTypes.SetLoading, params: false });
          break;
        }
        case "LOAD_ERROR": {
          // NOTE: The iframe shows its own warning modal via showWarningMessage.
          //   Just dismiss the parent loading spinner here.
          handleAction({ actionType: DriveUIActionTypes.SetLoading, params: false });
          break;
        }
        case "LOAD_ERROR_DISMISSED": {
          // NOTE: User clicked OK/Cancel on the iframe's warning modal — close the file viewer.
          handleAction({ actionType: DriveUIActionTypes.ResetFileEntry });
          break;
        }
        case "SECURITY_WARNING": {
          // NOTE: The iframe detected a postMessage from an unexpected origin and forwarded it here for logging.
          const warning_origin = typeof event.data.origin === "string" ? event.data.origin : "unknown";
          dispatch(uiActions.handleRequestErrors(
            new Message(DriveErrorMessages.error_invalid_pdf_viewer_origin.replace(MessageAnchors.origin, warning_origin), MessageHandlerDisplayType.logger)
          ));
          break;
        }
        default:
          break;
      }
    }

    window.addEventListener("message", handleFrameMessage);
    return () => window.removeEventListener("message", handleFrameMessage);
    // NOTE: is_view_only is in the dep array so the VIEWER_READY handler captures the latest
    //   value when building the LOAD_FILE payload. This matters if is_view_only changes before
    //   VIEWER_READY is received (e.g., permissions resolve after the iframe loads).
  }, [file, is_view_only, type, handleAction]);

  useEffect(() => {
    // We need to update the features if the view only status changes
    // while the user is viewing the document (via cloud_lock permission changes)
    // NOTE: Web mode — delegate to the iframe via postMessage. The frame handles the
    //   no-op case when the viewer isn't initialized yet.
    if (AppConfiguration.buildForWeb()) {
      iframe_ref.current?.contentWindow?.postMessage({ type: "UPDATE_PERMISSIONS", is_view_only }, "*");
      return;
    }
    if (instance) {
      // we can assume that if save as is disabled, then the others are too.
      const is_save_as_disabled = instance.UI.isElementDisabled("saveAsButton");
      if (is_view_only && !is_save_as_disabled) {
        instance.UI.disableFeatures(PDFTRON_CONFIG.view_only_features);
        instance.UI.disableElements(PDFTRON_CONFIG.view_only_elements);
      } else if (!is_view_only && is_save_as_disabled) {
        instance.UI.enableFeatures(PDFTRON_CONFIG.view_only_features);
        instance.UI.enableElements(PDFTRON_CONFIG.view_only_elements);
      }
    }

  }, [instance, is_view_only]);

  useEffect(() => {
    return () => {
      instance?.Core.documentViewer.dispose();
    };
  }, [instance]);

  // Description: Handle document loaded event in app build mode. Forces single-page layout
  //   for Excel and PowerPoint; PDFs and Word docs use the default continuous scroll layout.
  function _wvDocumentLoadedHandler(wv_instance: WebViewerInstance): void {
    const { UI } = wv_instance;
    const LayoutMode = UI.LayoutMode;
    switch (type) {
      case DriveItemType.Word:
        break;
      case DriveItemType.PDF:
        break;
      case DriveItemType.PowerPoint:
      case DriveItemType.Excel:
        UI.setLayoutMode(LayoutMode.Single);
        break;
      case DriveItemType.Image:
        break;
    }
    handleAction({ actionType: DriveUIActionTypes.SetLoading, params: false });
  }

  // Description: Load document into PDFTron and set up error handling for app build mode.
  //   LOAD_ERROR is the correct event for catching PDFTron's internal error modal (e.g., std::bad_alloc).
  function _loadDocument(wv_instance: WebViewerInstance, doc: Blob | Core.PDFNet.PDFDoc) {
    const { UI, Core: wv_core } = wv_instance;
    UI.loadDocument(doc, { filename: file.name, officeOptions: { disableBrowserFontSubstitution: true } });
    wv_core.documentViewer.addEventListener("documentLoaded", () => _wvDocumentLoadedHandler(wv_instance));
    // NOTE: Dismiss both PDFTron's loading modal and the parent loading spinner.
    function dismissLoading() {
      UI.closeElements(["loadingModal"]);
      handleAction({ actionType: DriveUIActionTypes.SetLoading, params: false });
    }
    wv_core.documentViewer.addEventListener("loaderror", dismissLoading);
    UI.addEventListener("error", dismissLoading);
    // NOTE: LOAD_ERROR catches PDFTron's internal error modal (e.g., "Exception during conversion: std::bad_alloc").
    //   Close both PDFTron modals (error + loading), dismiss the parent spinner, and show a friendly warning.
    UI.addEventListener(UI.Events.LOAD_ERROR, (err: unknown) => {
      UI.closeElements(["errorModal", "loadingModal", "progressModal"]);
      // NOTE: PDFTron's ProgressModal lives inside its inner iframe and may persist after closeElements.
      //   Inject a CSS rule into the inner iframe to force-hide it once an error occurs.
      const iframe_doc = UI.iframeWindow?.document;
      if (iframe_doc) {
        const style = iframe_doc.createElement("style");
        style.textContent = ".ProgressModal, [data-element='progressModal'], [data-element='loadingModal'] { display: none !important; }";
        iframe_doc.head.appendChild(style);
      }
      handleAction({ actionType: DriveUIActionTypes.SetLoading, params: false });
      const raw_message = (err && typeof err === "object" && "message" in err) ? (err as { message: string }).message
        : (typeof err === "string" ? err : "");
      const is_conversion_error = raw_message.includes("bad_alloc") || raw_message.includes("Exception during conversion");
      UI.showWarningMessage({
        title: "Error Loading Document",
        message: is_conversion_error
          ? `"${file.name}" is too large or complex to preview in your browser. Please download it to view.`
          : (raw_message || "Document failed to load."),
        confirmBtnText: "OK",
        onConfirm: () => { handleAction({ actionType: DriveUIActionTypes.ResetFileEntry }); },
        onCancel: () => { handleAction({ actionType: DriveUIActionTypes.ResetFileEntry }); },
      });
    });
  }

  return AppConfiguration.buildForWeb() ?
    // NOTE: Web build mode — render the SW-isolated iframe. The iframe loads /pdf-viewer/frame which the SW serves with a relaxed CSP,
    //      allowing PDFTron to run without CSP violations in the parent document.
    <>
      {
      sw_ready &&
        <iframe
          allow="unload"
          ref={iframe_ref}
          src={PDFTRON_CONFIG.frame_url}
          title="PDF Viewer"
          className="viewer pdf-viewer-frame" />
      }
      <div id="pdf-marking">{PDFTRON_CONFIG.footer}</div>
    </> :
    // NOTE: App build mode — render PDFTron directly in the parent document (no CSP constraint).
    <>
      <div className="viewer" ref={viewer}></div>
      <div id="pdf-marking">{PDFTRON_CONFIG.footer}</div>
    </>;
}

export default React.memo(FileViewerComponent);
